import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api, ApiError } from "../../lib/api.js";
import { useAuth } from "../../lib/auth-context.js";
import { useT, type Translate } from "../../i18n/index.js";

interface Props {
  registrationMode: "open" | "invite" | "closed";
}

/**
 * An OAuth sign-in that the provider approved but this deployment refused. The
 * callback redirects here with the reason, since a browser mid-redirect has
 * nowhere to show an API error.
 */
const OAUTH_ERROR_KEYS: Record<string, Parameters<Translate>[0]> = {
  registration_closed: "auth.registrationClosed",
  email_unverified: "auth.emailUnverified",
  oauth_failed: "auth.oauthFailed",
};

const s = {
  form: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "var(--space-md)",
  },
  label: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "0.375rem",
    fontSize: "var(--text-sm)",
    fontWeight: 600,
    color: "var(--text-mid)",
  },
  input: {
    padding: "0.875rem var(--space-md)",
    fontSize: "var(--text-base)",
    borderRadius: "var(--radius-md)",
    borderWidth: "2px",
    borderStyle: "solid",
    borderColor: "var(--border)",
    background: "var(--bg)",
    color: "var(--text)",
    width: "100%",
    outline: "none",
    transition: "border-color var(--duration-fast) var(--ease-out)",
  },
  inputFocus: {
    borderColor: "var(--brand)",
  },
  btn: {
    padding: "1rem",
    fontSize: "var(--text-base)",
    fontWeight: 700,
    cursor: "pointer",
    borderRadius: "var(--radius-md)",
    background: "var(--brand)",
    color: "#fff",
    border: "none",
    minHeight: "58px",
    marginTop: "var(--space-xs)",
    transition: "background var(--duration-fast) var(--ease-out)",
  },
  error: {
    color: "var(--error)",
    fontSize: "var(--text-sm)",
    margin: 0,
    padding: "0.75rem 1rem",
    background: "var(--error-lt)",
    borderRadius: "var(--radius-sm)",
    fontWeight: 500,
  },
  linkRow: {
    textAlign: "center" as const,
    margin: 0,
    fontSize: "var(--text-sm)",
    color: "var(--text-mid)",
  },
};

export function LoginForm({ registrationMode }: Props) {
  const t = useT();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [focused, setFocused] = useState<string | null>(null);
  const { refetch } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const oauthErrorKey = OAUTH_ERROR_KEYS[searchParams.get("error") ?? ""];
  const oauthError = oauthErrorKey ? t(oauthErrorKey) : undefined;
  const shownError = error ?? oauthError ?? null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);
    try {
      await api.post("/auth/login", { email, password });
      await refetch();
      navigate("/admin/quizzes");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("auth.signInFailed"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputStyle = (name: string) => ({
    ...s.input,
    ...(focused === name ? s.inputFocus : {}),
  });

  return (
    <form onSubmit={handleSubmit} style={s.form}>
      {shownError && (
        <p role="alert" style={s.error}>
          {shownError}
        </p>
      )}
      <label style={s.label}>
        {t("auth.email")}
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onFocus={() => setFocused("email")}
          onBlur={() => setFocused(null)}
          required
          autoComplete="email"
          style={inputStyle("email")}
        />
      </label>
      <label style={s.label}>
        {t("auth.password")}
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onFocus={() => setFocused("password")}
          onBlur={() => setFocused(null)}
          required
          autoComplete="current-password"
          style={inputStyle("password")}
        />
      </label>
      <button
        type="submit"
        className="btn-tactile"
        disabled={isSubmitting}
        style={{ ...s.btn, opacity: isSubmitting ? 0.7 : 1, cursor: isSubmitting ? "not-allowed" : "pointer" }}
      >
        {isSubmitting ? t("auth.signingIn") : t("auth.signIn")}
      </button>
      {registrationMode !== "closed" && (
        <p style={s.linkRow} className="auth-link-row">
          {t("auth.noAccount")} {" "}
          <Link to="/register" className="auth-inline-link">
            {t("auth.createOne")}
          </Link>
        </p>
      )}
    </form>
  );
}
